import i18next from 'i18next';

import apiOffers from 'api/offers';
import downloadNamedFile from 'services/files/downloadNamedFile';
import { finishLoading, startLoading } from 'store/loading/actions';
import { addMessage } from 'store/messages/actions';
import store from 'store/store';
import { DEFAULT_LANGUAGE } from 'services/translations/languages';
import { ADDITIONAL_OFFER } from '../../../services/enums/offers/offerTypes';
import openPdfInNewTab from '../../../services/files/openPdfInNewTab';

export interface IOfferRecipient {
  companyName: string;
  id: number;
  personName: string;
}

/**
 * Recipients of the offer in the order they were added: one per customer contact row, i.e.
 * a company with or without a contact person.
 */
export const getOfferRecipients = (
  customerContacts: any[]
): IOfferRecipient[] =>
  customerContacts
    .filter((customerContact) => customerContact?.contact != null)
    .map((customerContact) => ({
      companyName: customerContact.contact.companyName ?? '',
      id: customerContact.id,
      personName: customerContact.contactPerson
        ? `${customerContact.contactPerson.firstName ?? ''} ${
            customerContact.contactPerson.lastName ?? ''
          }`.trim()
        : '',
    }));

// "<type> <target> (<company>)", with the person when the company has several recipients
const getFileName = (
  typeName: string,
  targetName: string,
  recipient: IOfferRecipient,
  recipients: IOfferRecipient[]
) => {
  const sameCompany = recipients.filter(
    (other) => other.companyName === recipient.companyName
  );
  const recipientName =
    sameCompany.length > 1 && recipient.personName !== ''
      ? `${recipient.companyName}, ${recipient.personName}`
      : recipient.companyName;

  // Characters Windows does not allow in file names
  return `${typeName} ${targetName} (${recipientName}).pdf`.replace(
    /[\\/:*?"<>|]/g,
    '-'
  );
};

/**
 * Opens the offer PDF. An offer sent to several recipients (e.g. a preliminary offer) is
 * printed as a separate file for each recipient, each showing only that recipient, named
 * "<Offer type> <target name> (<company>).pdf".
 */
const openOfferPdf = async (id: number, offerType: string): Promise<void> => {
  store.dispatch(startLoading('offer-pdf'));

  try {
    if (offerType === ADDITIONAL_OFFER) {
      const response = await apiOffers.additionalOffers.pdfs.get(id);
      openPdfInNewTab(response.data, response.headers);
      return;
    }

    const offer = await apiOffers.getOffer(id);
    const recipients = getOfferRecipients(offer.customerContacts ?? []);

    if (recipients.length <= 1) {
      const response = await apiOffers.pdfs.get(id);
      openPdfInNewTab(response.data, response.headers);
      return;
    }

    // The files go to the customer, so they are named in the language of the offer
    const offerLanguage = offer.language ?? DEFAULT_LANGUAGE;
    await i18next.loadLanguages(offerLanguage);
    const typeName = i18next.getFixedT(offerLanguage)(
      `offers:types.${offer.type}`
    );

    // Sequentially, so the browser handles the downloads one at a time
    // eslint-disable-next-line no-restricted-syntax
    for (const recipient of recipients) {
      // eslint-disable-next-line no-await-in-loop
      const response = await apiOffers.pdfs.get(id, recipient.id);
      downloadNamedFile(
        response.data,
        'application/pdf',
        getFileName(typeName, offer.targetName, recipient, recipients)
      );
    }

    store.dispatch(
      addMessage(
        i18next.t('offers:PDFs downloaded', { count: recipients.length })
      )
    );
  } catch (error) {
    store.dispatch(addMessage(i18next.t('offers:pdfPreview.Load failed')));
  } finally {
    store.dispatch(finishLoading('offer-pdf'));
  }
};

export default openOfferPdf;
